import { createAsyncThunk } from '@reduxjs/toolkit';
import { UrlRankRepositoryImpl } from '../Data/repositories/UrlRankRepositoryImpl';
import { LookupUrlRankUseCase } from '../Domain/UseCase/LookupUrlRankUseCase';
import {
  UrlRankRequest,
  UrlRankResult,
  UrlRankError,
} from '../Model/UrlRank';
import {
  setUrlRankLoading,
  setUrlRankProgress,
  setUrlRankResult,
  setUrlRankError,
} from './reducers';

const repo = new UrlRankRepositoryImpl();
const useCase = new LookupUrlRankUseCase(repo);

let activeAbort: AbortController | null = null;

export const lookupUrlRank = createAsyncThunk<
  UrlRankResult,
  UrlRankRequest,
  { rejectValue: UrlRankError }
>(
  'urlRank/lookup',
  async (request, { dispatch, rejectWithValue }) => {
    if (activeAbort) {
      activeAbort.abort();
    }
    const controller = new AbortController();
    activeAbort = controller;

    try {
      dispatch(setUrlRankLoading(true));

      const result = await useCase.execute(request, {
        signal: controller.signal,
        onProgress: (progress) => {
          if (!controller.signal.aborted) {
            dispatch(setUrlRankProgress(progress));
          }
        },
      });

      if (controller.signal.aborted) {
        return rejectWithValue({
          type: 'API_ERROR',
          message: '조회가 취소되었습니다.',
        });
      }

      if (result.isOk()) {
        dispatch(setUrlRankResult(result.value));
        return result.value;
      }

      if (result.error.message === '조회가 취소되었습니다.') {
        return rejectWithValue(result.error);
      }

      dispatch(setUrlRankError(result.error));
      return rejectWithValue(result.error);
    } catch (error) {
      if (controller.signal.aborted) {
        return rejectWithValue({
          type: 'API_ERROR',
          message: '조회가 취소되었습니다.',
        });
      }
      const fallbackError: UrlRankError = {
        type: 'API_ERROR',
        message: 'URL 순위 조회 중 오류가 발생했습니다.',
        details: error instanceof Error ? error.message : String(error),
      };
      dispatch(setUrlRankError(fallbackError));
      return rejectWithValue(fallbackError);
    } finally {
      if (activeAbort === controller) {
        activeAbort = null;
      }
      if (!controller.signal.aborted) {
        dispatch(setUrlRankLoading(false));
      }
    }
  },
);

export const cancelUrlRankLookup = () => {
  if (activeAbort) {
    activeAbort.abort();
    activeAbort = null;
  }
};
